import type { StaffRole } from '@planuze/admin-contracts';
import {
  Breadcrumb,
  Page,
  PageActions,
  PageContent,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@planuze/ui';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { DetailPageState } from '../components/detail-page-state.js';
import { ManifestDiffCard } from '../components/pack-submissions/manifest-diff-card.js';
import { ScanFindingsCard } from '../components/pack-submissions/scan-findings-card.js';
import { SubmissionActionsBar } from '../components/pack-submissions/submission-actions-bar.js';
import { SubmissionOverviewCard } from '../components/pack-submissions/submission-overview-card.js';
import { SubmissionTimeline } from '../components/pack-submissions/submission-timeline.js';
import { VersionCompareCard } from '../components/pack-submissions/version-compare-card.js';
import { VersionHistoryCard } from '../components/pack-submissions/version-history-card.js';
import { usePackSubmissionDetailContent } from '../content/sections/pack-submission-detail.js';
import { usePackSubmissionDetail } from '../hooks/use-pack-submissions.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

const routeApi = getRouteApi('/authenticated/pack-submissions/$id');
/* Quem pode acionar o scan/decisões (espelha WRITE_ROLES do admin-service). */
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

/* Tela de detalhe da submissão (PUB-11 / ADR-0462). Page fina — orquestra estados + Tabs;
   o render de cada bloco vive nos subcomponentes (§4.7). */
export function PackSubmissionDetailPage() {
  const copy = usePackSubmissionDetailContent();
  const { id } = routeApi.useParams();
  const navigate = useNavigate();
  const { data: submission, isLoading, isError } = usePackSubmissionDetail(id);
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);

  const renderHeader = (label: string) => (
    <PageHeader>
      <PageTitleBlock>
        <Breadcrumb
          ariaLabel={copy.breadcrumbAriaLabel}
          items={[
            {
              label: copy.submissionsBreadcrumb,
              onClick: () => {
                navigate({ to: '/pack-submissions' }).catch(() => undefined);
              },
            },
            { label },
          ]}
        />
      </PageTitleBlock>
    </PageHeader>
  );

  if (isError) {
    return (
      <DetailPageState
        header={renderHeader(copy.fallbackTitle)}
        kind="error"
        message={copy.notFoundError}
      />
    );
  }
  if (isLoading || submission === undefined) {
    return <DetailPageState header={renderHeader(copy.fallbackTitle)} kind="loading" message="" />;
  }
  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <Breadcrumb
            ariaLabel={copy.breadcrumbAriaLabel}
            items={[
              {
                label: copy.submissionsBreadcrumb,
                onClick: () => {
                  navigate({ to: '/pack-submissions' }).catch(() => undefined);
                },
              },
              {
                label: submission.packId
                  ? `${submission.packId} v${submission.version}`
                  : copy.fallbackTitle,
              },
            ]}
          />
          <PageTitle>
            {submission.packId} <span className="text-muted-foreground">v{submission.version}</span>
          </PageTitle>
        </PageTitleBlock>
        <PageActions>
          <SubmissionActionsBar submissionId={submission.id} status={submission.status} />
        </PageActions>
      </PageHeader>
      <PageContent>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">{copy.overviewTab}</TabsTrigger>
            <TabsTrigger value="scan">{copy.scanTab}</TabsTrigger>
            <TabsTrigger value="timeline">{copy.timelineTab}</TabsTrigger>
            <TabsTrigger value="versions">{copy.versionsTab}</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <SubmissionOverviewCard submission={submission} />
          </TabsContent>
          <TabsContent value="scan">
            <ScanFindingsCard
              scanResults={submission.scanResults}
              submissionId={submission.id}
              canManage={canManage}
            />
          </TabsContent>
          <TabsContent value="timeline">
            <SubmissionTimeline transitions={submission.transitions} />
          </TabsContent>
          <TabsContent value="versions" className="space-y-4">
            <ManifestDiffCard manifestDiff={submission.manifestDiff} />
            <VersionHistoryCard versions={submission.previousVersions} />
            <VersionCompareCard
              submissionId={submission.id}
              currentVersion={submission.version}
              previousVersions={submission.previousVersions}
            />
          </TabsContent>
        </Tabs>
      </PageContent>
    </Page>
  );
}
